// @ts-strict-ignore
import * as React from 'react';
import {useEffect, useState} from 'react';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import Highcharts from 'highcharts';
import clsx from 'clsx';

import {
  getLineChartData,
  getLineSeries,
  getOtherSegmentData,
  LineChartData,
  OtherSegmentData,
} from 'common/report/insights/charts/insights-line-chart/util';
import {PositionParent} from 'common/components/drop-menu';
import {IChartDataItem, LineChartYAxisOptions, XAxisOptions} from 'common/charts/visualization/line-chart/types';
import {ForecastValue, ParamsAndResults} from 'common/report/types';
import {FlatSegment, InsightsQueryResults} from 'common/report/insights/util/types';
import {ChartType, InsightsBookmarkParams, SortConfig} from 'common/types/reports/bookmark';
import {AxisAssignment, PopoverCallback} from 'common/charts/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';
import {getWorkspaceId, isPublicMode} from 'common/report/util';
import {dataGroupStore} from 'common/report/stores/data-groups';
import {ProjectTimezone} from 'common/report/stores/types';
import {
  canShowOtherSegment,
  getDisplayOptions,
  getSortConfig,
  getSortedIsoDateKeysFromTimeseries,
  insightsBookmarkParamsToChartParams,
  lookupSegment,
  memoizedGetHighchartsTimestamp,
  memoizedGetTimestamp,
} from 'common/report/insights/util';
import {
  INSIGHTS_LINE_CSV_EXPORT_LIMIT,
  REPORT_LIMITS,
  ReportName,
  RESULTS_OVERALL_SUM_KEY,
} from 'common/report/constants';
import {isCustomCsvLimitEnabled} from 'common/widgets/csv-export-modal/utils';
import {
  ClauseType,
  computeMetricClauseByKey,
  LineChartTooltipStat,
  renderLineChartTooltip,
  RenderLineChartTooltipParams,
  ShowClauseData,
} from 'common/report/insights/util/tooltip';
import {OTHER_SEGMENT_VALUE} from 'common/report/insights/util/constants';
import {LiftType} from 'common/report/util/lift/types';
import {getHighchartsColorString} from 'common/charts/visualization/line-chart/utils';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {deselectPoint, selectPoint} from 'common/report/util/highcharts';
import {
  getDisplayOptionsFromLineChartYAxisOptions,
  getMetricFormattingDefaultsFromChartParams,
  mergeDisplayOptions,
  metricFormatterFunctionForAxis,
} from 'common/report/insights/charts/util';
import {
  getDefaultPrimaryAndSecondaryAxisOptions,
  getYAxisDefaultOptions,
} from 'common/query-builder/chart-customization/utils';
import {formatMetricNumberWithUnits} from 'common/util';
import {SpinnerReact} from 'common/components/spinner';
import {colors} from 'common/util/colors';
import {TimeSeriesType} from 'common/charts/visualization/line-chart/enums';
import {renamePropertyValue} from 'common/report/util/properties';
import {ContextMenuReact} from 'common/widgets/context-menu';
import {useTranslation} from 'common/i18n/hooks';
import {SegmentLegendHeader} from 'common/charts/misc/segment-legend-header';
import {LineChartReact} from 'common/charts/visualization/line-chart';
import {isRetentionOnlyInsightsBookmark} from 'common/report/insights/models/utils';
import {EmptyDataReact} from 'common/charts/misc/empty-data';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import {IronContextRegistry} from 'common/report/context-registry';
import {withStyles} from 'common/util/react';

import styles from './index.module.styl';
import css from './index.module.styl?inline';

const LEFT_MARGIN_WITHOUT_YAXIS = 25;

export type OnPopoverActionProps = {detail: {action: string; pointData: RenderLineChartTooltipParams}};

export type InsightsLineChartProps = {
  elref?: string;

  segmentFilterController: SegmentFilterController;

  // use a single prop for bookmark params and results so they can be updated together atomically
  paramsAndResults: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null;
  popoverCallback?: PopoverCallback;
  dataGroups?: Record<string, DataGroup>;
  formatXAxisLabel?: (value: number, timeUnit?: TimeUnit) => string;

  failed?: boolean;
  hideChartLines?: boolean;
  hideLegend?: boolean;
  loaded: boolean;
  showAnnotations?: boolean;
  showFirstLastTick?: boolean;
  timezone?: string;
  utcOffset?: number;
  xAxisOptions?: XAxisOptions;
  primaryYAxisOptions?: Highcharts.YAxisOptions;
  secondaryYAxisOptions?: Highcharts.YAxisOptions;
  emptyDataContainerClassName?: string;

  onPopoverAction?: (props: OnPopoverActionProps) => void;
};

export const InsightsLineChart: React.FC<
  Pick<IronContextRegistry, `currentProject` | `i18n` | `metrics`> & InsightsLineChartProps
> = (props) => {
  const [lineChartData, setLineChartData] = useState<LineChartData | null>(null);
  /** clicked highcharts point object to display a popover for */
  const [popoverSelection, setPopoverSelection] = useState<Highcharts.Point | null>(null);
  /** holds positioning of the popover for the active highcharts point */
  const [popoverPositionParent, setPopoverPositionParent] = useState<PositionParent>();

  const [project, setProject] = useState<CurrentProject | null>(null);
  const [projectTimezone, setProjectTimezone] = useState<ProjectTimezone | null>(null);
  // optional dataGroups props in case dataGroupStore is not available (e.g. Public Dashboards)
  const [dataGroups, setDataGroups] = useState<Record<string, DataGroup> | null>(null);

  const [isConnected, setIsConnected] = useState(false);

  const [paramsAndResults, setParamsAndResults] = useState<ParamsAndResults<
    InsightsBookmarkParams,
    InsightsQueryResults
  > | null>(null);
  const bookmarkParams = paramsAndResults?.params;
  const results = paramsAndResults?.results;

  const hasProjectTimeAttributes = (): boolean => {
    return Boolean(props.timezone);
  };

  const getCurrentProject = (): CurrentProject | undefined => {
    return isPublicMode() ? undefined : props.currentProject.get().value;
  };

  const getProjectTimezone = (): ProjectTimezone | null => {
    if (hasProjectTimeAttributes()) {
      return {
        utcOffset: props.utcOffset,
        timezone: props.timezone,
      };
    }
    return projectTimezone;
  };

  const hasRequiredParams = (): boolean => {
    // Props may have been set before connectedCallback() fires, therefore need to ensure
    // this component is connected before accessing a context (e.g. props.segmentFilterController)
    return !!(isConnected && getProjectTimezone() && bookmarkParams && results);
  };

  const getDataGroups = (): Record<string, DataGroup> => {
    if (dataGroups) {
      return dataGroups;
    } else {
      return dataGroupStore.value ?? {};
    }
  };

  const updateTimezoneFromContext = () => {
    // check public dashboard
    if (!dataGroups) {
      const currentProject = getCurrentProject();
      if (currentProject) {
        setProjectTimezone({
          utcOffset: currentProject.utcOffset,
          timezone: currentProject.timezone,
        });
      }
    }
  };

  const updateLineChartData = (): void => {
    if (hasRequiredParams()) {
      const newLineChartData = getLineChartData(
        getProjectTimezone(),
        // extracting only the actual values used allows for better memoization
        insightsBookmarkParamsToChartParams(bookmarkParams),
        results,
        getDataGroups(),
        props.metrics,
        props.i18n,
      );
      setLineChartData(newLineChartData);

      const {meta} = results;
      const {apiResultsTruncated, queryLimits} = meta;

      props.segmentFilterController.loadSections(
        [
          {
            items: newLineChartData.legendSegmentItems,
            metadata: newLineChartData.legendIdToIndexMap, // Insights app is expected to use this mapping to persist legend selection indices to the URL
            apiResultsTruncated,
            tableColumns: newLineChartData.tableColumns,
            readOnlyItems: newLineChartData.readOnlyLegendItems,
          },
        ],
        {
          defaultNumSelections:
            REPORT_LIMITS[ReportName.Insights].segments[bookmarkParams.displayOptions.chartType].default,
          maxNumSelections: REPORT_LIMITS[ReportName.Insights].segments[bookmarkParams.displayOptions.chartType].max,
          tableSortConfig: getSortConfig({
            bookmarkSortConfig: bookmarkParams.sorting,
            headerCount: results.headers.length,
          })[ChartType.Line] as SortConfig, // Column chart shares sort config with line
        },
      );

      props.segmentFilterController.segmentationQueryLimit = queryLimits?.limit;
      props.segmentFilterController.exportSegmentationQueryLimit = isCustomCsvLimitEnabled(getCurrentProject())
        ? INSIGHTS_LINE_CSV_EXPORT_LIMIT
        : REPORT_LIMITS[ReportName.Insights].csvExport;
    } else {
      setLineChartData(null);
    }
  };

  const isEndDateIncomplete = () => {
    if (!lineChartData.lineChartSeries.length) {
      return false;
    }
    const hasIncompleteData = lineChartData.lineChartSeries[0].incompleteLength ?? 0;
    return isRetentionOnlyInsightsBookmark(bookmarkParams) && hasIncompleteData > 0;
  };

  const hasForecastComparison = () => lineChartData.forecastComparisonSegmentLookup?.size > 0;

  function formatTooltipStatLogic<ValueT = number>(
    point: Highcharts.Point,
    segment: FlatSegment<ValueT>,
  ): LineChartTooltipStat<ValueT> {
    const dates = getSortedIsoDateKeysFromTimeseries(segment.value);
    const value: ValueT = segment.value[dates[point.index]];
    const secondaryValue: ValueT = segment.secondaryValue ? segment.secondaryValue[dates[point.index]] : null;

    const timestamp = memoizedGetTimestamp(dates[point.index]);
    const highchartsTimestamp = memoizedGetHighchartsTimestamp(
      dates[point.index],
      getProjectTimezone().timezone,
      getProjectTimezone().utcOffset,
    );

    return {
      highchartsTimestamp,
      secondaryValue,
      timestamp,
      value,
    };
  }

  const formatTooltipStat = (point: Highcharts.Point, segment: FlatSegment): LineChartTooltipStat => {
    const stat = formatTooltipStatLogic(point, segment);
    stat.value = stat.value ?? 0;
    if (stat.secondaryValue) {
      stat.secondaryValue = stat.secondaryValue ?? 0;
    }
    return stat;
  };

  const updatePersistedLegendSelections = () => {
    const individualSelectedSegments = bookmarkParams?.legend?.individualSelectedSegments;

    if (isConnected && individualSelectedSegments) {
      props.segmentFilterController.loadFromSavedLegend(
        individualSelectedSegments ? [individualSelectedSegments] : null,
      );
    }
  };

  const resourceCustomSubscriptions = new Map([
    [
      CurrentProjectResource,
      () => {
        // this component disables project & dataGroup Stores if equivalent attributes are specified, this may be required
        // in environments where Stores don't work, eg. Public Dashboards
        if (!dataGroups && !hasProjectTimeAttributes()) {
          const currentProject = getCurrentProject();
          if (project?.id !== currentProject.id) {
            const projectId = currentProject.id;
            const workspaceId = parseInt(getWorkspaceId(), 10);
            // Note: data groups are only used in tooltips and (as of writing) do not need to trigger state updates/renders or annotated with people properties
            dataGroupStore.fetch({projectId, workspaceId}); // async, no need to wait
          }
          if (!isEqual(project, currentProject)) {
            setProject(currentProject);
            updateTimezoneFromContext();
            updateLineChartData();
          }
        }
      },
    ],
  ]);

  useEffect(() => {
    updateTimezoneFromContext();
    props.currentProject.subscribe(resourceCustomSubscriptions.get(CurrentProjectResource));
    setIsConnected(true);
    return () => {
      props.currentProject.unsubscribe(resourceCustomSubscriptions.get(CurrentProjectResource));
    };
  }, []);

  useEffect(() => {
    if (!isEqual(paramsAndResults, props.paramsAndResults)) {
      setParamsAndResults(props.paramsAndResults);
    }
  }, [props.paramsAndResults]);

  useEffect(() => {
    if (paramsAndResults) {
      updateLineChartData();
      updatePersistedLegendSelections();
    }
  }, [paramsAndResults, dataGroups]);

  useEffect(() => {
    setDataGroups(props.dataGroups);
  }, [props.dataGroups]);

  const formatTooltipForecastValueStat = (
    point: Highcharts.Point,
    segment: FlatSegment<ForecastValue>,
  ): LineChartTooltipStat<ForecastValue> => {
    // for charts with forecast comparisons, find the true index for the point first since
    // anomaly marker points don't map 1 to 1 on the main series
    const forecastPointIndex = lineChartData.lineChartSeries[0].data.findIndex((dataItem) => dataItem[0] === point.x);
    const forecastPoint = {...point, index: forecastPointIndex} as Highcharts.Point;
    const stat = formatTooltipStatLogic<ForecastValue>(forecastPoint, segment);
    stat.value = stat.value ?? {yhat_lower: 0, yhat_upper: 0};
    return stat;
  };

  const generatePointTooltipParams = (
    highchartsContext: {
      point: Highcharts.Point;
      series: Highcharts.Series;
      percentage: number;
    },
    otherSegmentData?: OtherSegmentData,
  ): RenderLineChartTooltipParams => {
    const {point, series, percentage} = highchartsContext;
    const segmentKey = series.options.name; // use name from options because highcharts randomly names series with an empty string name
    const showPreviousValueDelta = !Number.isFinite(percentage) && point.index > 0;
    const {segmentLookup} = lineChartData;
    const reportSectionsMetadata = results.meta.report_sections;
    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: reportSectionsMetadata,
      bookmarkParams,
      dataGroups: Object.values(getDataGroups()),
    });

    let mainSegment: FlatSegment = segmentLookup.get(segmentKey);
    let metricClauseData = metricClauseByKey[mainSegment?.path?.[0]];
    if (segmentKey === OTHER_SEGMENT_VALUE) {
      mainSegment = otherSegmentData.segment;
      metricClauseData = {
        clauseType: ClauseType.Other,
        metricClauses: otherSegmentData.metricKeys.map((key) => metricClauseByKey[key]),
        metricKey: OTHER_SEGMENT_VALUE,
      };
    }
    const metricMetadata = reportSectionsMetadata.show.find((metadata) => {
      return metadata.metric_key === mainSegment?.path?.[0];
    });

    const mainDates = getSortedIsoDateKeysFromTimeseries(mainSegment.value);
    const deltaStat: LineChartTooltipStat = showPreviousValueDelta
      ? {
          value: mainSegment.value[mainDates[point.index - 1]],
        }
      : null;

    const stat: LineChartTooltipStat = {
      ...formatTooltipStat(point, mainSegment),
      dateRange: {
        fromDate: results.date_range.from_date,
        toDate: results.date_range.to_date,
      },
      segmentKey: `arbitrary`,
    };

    let timeComparisonStat: null | LineChartTooltipStat = null;
    let liftComparisonStat: null | LineChartTooltipStat = null;
    let baselineComparisonStat: null | LineChartTooltipStat = null;
    let overallComparisonStat: null | LineChartTooltipStat = null;
    let forecastComparisonStat: null | LineChartTooltipStat<ForecastValue> = null;

    if (lineChartData.liftComparisonSegmentLookup.has(segmentKey)) {
      const liftComparisonSegment = lookupSegment(
        segmentKey,
        lineChartData.liftComparisonSegmentLookup,
        mainSegment.path,
      );

      liftComparisonStat = {
        ...formatTooltipStat(point, liftComparisonSegment),
        // we only want to display the leaf segment name
        segmentKey: segmentKey.split(`,`).slice(-1)[0],
      };

      const {type}: {type: LiftType} = results.meta.liftComparison;

      switch (type) {
        case LiftType.Time:
          // Time will be calculated with the timeComparisonSegmentLookup
          break;
        case LiftType.Overall:
          overallComparisonStat = {
            ...formatTooltipStat(point, lookupSegment(RESULTS_OVERALL_SUM_KEY, segmentLookup, mainSegment.path)),
            segmentKey: `Overall`,
          };
          break;
        case LiftType.Segment: {
          const baselineSegmentKey = [
            ...segmentKey.split(`,`).slice(0, -1),
            results.meta.liftComparison.groupValue || results.meta.liftComparison.cohort.name,
          ].join(`,`);
          if (baselineSegmentKey) {
            baselineComparisonStat = {
              ...formatTooltipStat(point, lookupSegment(baselineSegmentKey, segmentLookup, mainSegment.path)),
              segmentKey: baselineSegmentKey,
            };
          }
          break;
        }
      }
    }

    if (lineChartData.timeComparisonSegmentLookup.has(segmentKey)) {
      const comparisonSegment = lookupSegment(segmentKey, lineChartData.timeComparisonSegmentLookup);
      timeComparisonStat = {
        ...formatTooltipStat(point, comparisonSegment),
        dateRange: {
          fromDate: results.time_comparison.date_range.from_date,
          toDate: results.time_comparison.date_range.to_date,
        },
      };
    }

    if (lineChartData.forecastComparisonSegmentLookup.has(segmentKey)) {
      const forecastSegment = lookupSegment<ForecastValue>(segmentKey, lineChartData.forecastComparisonSegmentLookup);
      forecastComparisonStat = formatTooltipForecastValueStat(point, forecastSegment);
      // for charts with forecast comparisons, use the point values for the stat since
      // anomaly marker points don't map 1 to 1 on the main series
      stat.value = point.y;
      stat.highchartsTimestamp = point.x;
    }

    const displayOptions = getDisplayOptions(bookmarkParams.sections, bookmarkParams.displayOptions);
    const isFirstInterval = point.index === 0;
    const isLastInterval = point.index === series.data.length - 1;
    const isIncomplete = series.data.length - point.index <= metricMetadata?.incomplete_length;
    const colorString = getHighchartsColorString(series.color);

    return {
      headers: results.headers,
      metricClauseData,
      metricsResource: props.metrics,
      // this populates the breakdown subheading, exclude the primary metric in the segment array
      segments: mainSegment.path.slice(1),
      stat,
      deltaStat,
      timeComparisonStat,
      liftComparisonStat,
      baselineComparisonStat,
      overallComparisonStat,
      forecastComparisonStat,
      percent: percentage,
      timeUnit: displayOptions.timeUnit,
      isFirstInterval,
      isLastInterval,
      isIncomplete,
      titleColor: colorString,
      timezone: getProjectTimezone()?.timezone,
      utcOffset: getProjectTimezone().utcOffset,
      // @ts-ignore Property 'isComparisonItem' does not exist on type 'SeriesOptionsType'.
      isComparisonItem: series.userOptions.isComparisonItem,
      i18n: props.i18n,
    };
  };

  const formatYAxisLabel = (y: number, axis: AxisAssignment, yAxisOptions?: LineChartYAxisOptions): string => {
    const defaultOptions = getMetricFormattingDefaultsFromChartParams(bookmarkParams, true);
    const {defaultPrimaryOptions, defaultSecondaryOptions} = getDefaultPrimaryAndSecondaryAxisOptions(
      bookmarkParams,
      props.metrics,
    );

    const preFormatted = metricFormatterFunctionForAxis(bookmarkParams, axis)(y);
    const formatted = formatMetricNumberWithUnits(
      preFormatted,
      mergeDisplayOptions(
        {},
        defaultOptions,
        getDisplayOptionsFromLineChartYAxisOptions(
          axis === AxisAssignment.Primary ? defaultPrimaryOptions : defaultSecondaryOptions,
        ),
        getDisplayOptionsFromLineChartYAxisOptions(yAxisOptions),
      ),
      {abbreviateThousands: true},
    );
    return formatted;
  };

  const getPointDataFromSelection = () => {
    if (popoverSelection === null) {
      return null;
    }
    const point = popoverSelection;
    return generatePointTooltipParams({point, series: point.series, percentage: point.percentage});
  };

  const handlePopoverClose = () => {
    const selectedPoint = popoverSelection;
    if (selectedPoint) {
      deselectPoint(selectedPoint);
    }
    setPopoverSelection(null);
    setPopoverPositionParent(null);
  };

  const handlePopoverAction = (action: string) => {
    props.onPopoverAction?.({detail: {action, pointData: getPointDataFromSelection()}});
    handlePopoverClose();
  };

  // Arrow syntax is required to bind "this", which is otherwise changed by highcharts
  const handlePointClick = (point: Highcharts.Point, event: Highcharts.PointClickEventObject) => {
    const {series} = point;
    if (series.options.name === OTHER_SEGMENT_VALUE) {
      return;
    }

    const pointData = generatePointTooltipParams({point, series, percentage: point.percentage});
    const metricKey = (pointData.metricClauseData as ShowClauseData).metricKey;
    const hasPopoverActions = props.popoverCallback?.(metricKey, pointData.segments, {
      highchartsPointContext: point,
      pointData,
    }).length;

    if (hasPopoverActions) {
      selectPoint(point);
      setPopoverPositionParent({
        getBoundingClientRect: () => {
          const isVerticalBar = bookmarkParams.displayOptions.chartType === ChartType.Column;
          const targetEl: HTMLElement =
            point.graphic?.element ?? (point.series as any).markerGroup?.element?.firstChild;
          const targetRect = targetEl?.getBoundingClientRect();

          // close the popover when the line chart is resized and the selected point marker goes away
          if (targetRect === undefined) {
            handlePopoverClose();
            return new DOMRect(0, 0, 0, 0);
          } else if (isVerticalBar) {
            // makes sure the popover shifts over the Y axis to align it with the mouse cursor
            // this improves accessing the menu on larger columns
            return new DOMRect(targetRect.left, event.pageY, targetRect.width, targetRect.height);
          }

          return targetRect;
        },
      });
      setPopoverSelection(point);
    }
  };

  const getPopoverMenuItems = () => {
    const pointData = getPointDataFromSelection();
    const metricKey = (pointData.metricClauseData as ShowClauseData).metricKey;
    return props.popoverCallback(metricKey, pointData.segments, {
      highchartsPointContext: popoverSelection,
      isTimeComparisonSegment: pointData.isComparisonItem,
      pointData,
    });
  };

  const loaded = props.loaded && hasRequiredParams() && lineChartData;
  if (!loaded) {
    return (
      <div className={styles.root}>
        <SpinnerReact className={styles.mpSpinner} elref={`loadingSpinner`} />
      </div>
    );
  }

  if (isEmpty(results?.series)) {
    return (
      <div className={`${styles.root} ${props.emptyDataContainerClassName}`}>
        <EmptyDataReact />
      </div>
    );
  }

  // There will be multiple segments with the same segment name when using time comparison
  const seriesLookup = new Map<string, IChartDataItem[]>();
  lineChartData.lineChartSeries.forEach((segment) => {
    const segmentKey = segment.name;
    if (!seriesLookup.has(segmentKey)) {
      seriesLookup.set(segmentKey, []);
    }
    seriesLookup.get(segmentKey).push(segment);
  });

  const visibleChartSeries: IChartDataItem[] = props.segmentFilterController.getSelectedItems(0).flatMap((item) => {
    return seriesLookup.get(item.id).map((segment) => ({
      ...segment,
      color: props.segmentFilterController.getItemColor(0, item.id),
    }));
  });

  // include other segment in rendering cycle rather than data processing cycle
  // so that we have accurate values on each segment selection
  let otherSegmentData: OtherSegmentData;
  if (canShowOtherSegment(bookmarkParams)) {
    const allSegments = Array.from(lineChartData.segmentLookup.values());

    otherSegmentData = getOtherSegmentData(allSegments, props.segmentFilterController);
    if (!otherSegmentData.isAllZero) {
      visibleChartSeries.unshift(
        getLineSeries(
          otherSegmentData.percentagesSegment,
          colors.otherSegment.primary,
          getProjectTimezone(),
          results.meta.report_sections.show[0],
        ),
      );
    }
  }

  const visibleSegmentIds = visibleChartSeries.map((segment) => {
    return segment.name;
  });

  const bookmarkPrimaryYAxisOptions = bookmarkParams.displayOptions.primaryYAxisOptions;

  const metricsResource = props.metrics;

  const defaultPrimaryYAxisOptions = getYAxisDefaultOptions(bookmarkParams, AxisAssignment.Primary, metricsResource);
  const defaultSecondaryYAxisOptions = getYAxisDefaultOptions(
    bookmarkParams,
    AxisAssignment.Secondary,
    metricsResource,
  );

  const bookmarkSecondaryYAxisOptions = bookmarkParams.displayOptions.secondaryYAxisOptions;
  const bookmarkXAxisOptions = bookmarkParams.displayOptions.xAxisOptions;

  return (
    <div className={clsx(styles.root, `insights-line-chart`)} elref={props.elref}>
      {!props.hideLegend && (
        <SegmentLegendHeader
          className={styles.segmentLegendHeader}
          visibleSegmentIds={visibleSegmentIds}
          segmentFilterController={props.segmentFilterController}
        />
      )}
      <LineChartReact
        className={styles.lineChart}
        segmentFilterController={props.segmentFilterController}
        chartData={visibleChartSeries}
        chartMarginLeft={props.primaryYAxisOptions?.visible === false ? LEFT_MARGIN_WITHOUT_YAXIS : undefined}
        displayAnalysisType={lineChartData.chartAnalysisType}
        displayPlotStyle={lineChartData.chartPlotStyle}
        displaySeriesType={
          bookmarkParams.displayOptions.chartType === ChartType.Column ? TimeSeriesType.Column : TimeSeriesType.Line
        }
        displayStackingType={lineChartData.chartStackingType}
        disableAreaRangeMouseTracking={hasForecastComparison()}
        {...(props.showFirstLastTick && {
          formatTicksX: (min, max) => {
            return [min, max];
          },
        })}
        formatTooltip={(_x, _y, _series, _seriesIdx, tooltipContext): string => {
          const tooltipParams = generatePointTooltipParams(tooltipContext, otherSegmentData);
          return renderLineChartTooltip({
            ...tooltipParams,
            segments: tooltipParams.segments.map((value, index) =>
              renamePropertyValue(value, results.headers[index + 1], bookmarkParams.sections.group[index]),
            ),
            timezone: getProjectTimezone()?.timezone,
            i18n: props.i18n,
            metricsResource: props.metrics,
          });
        }}
        formatYAxisLabel={formatYAxisLabel}
        hideChartLines={props.hideChartLines}
        incompleteToDate={isEndDateIncomplete()}
        onHighChartClick={props.popoverCallback && ((a, b) => handlePointClick(a, b))}
        showAnnotations={props.showAnnotations}
        timeUnit={lineChartData.chartTimeUnit}
        timezone={getProjectTimezone().timezone}
        utcOffset={getProjectTimezone().utcOffset}
        defaultPrimaryYAxisOptions={defaultPrimaryYAxisOptions}
        defaultSecondaryYAxisOptions={defaultSecondaryYAxisOptions}
        primaryYAxisOptions={{
          ...bookmarkPrimaryYAxisOptions,
          ...props.primaryYAxisOptions,
        }}
        secondaryYAxisOptions={{
          ...bookmarkSecondaryYAxisOptions,
          ...props.secondaryYAxisOptions,
        }}
        zoomType={`x`}
        xAxisOptions={{
          ...props.xAxisOptions,
          ...bookmarkXAxisOptions,
        }}
        {...(props.formatXAxisLabel && {
          formatXAxisLabel: props.formatXAxisLabel,
        })}
      />
      {/* render point popover */}
      {popoverSelection && (
        <>
          <ContextMenuReact
            className={styles.mpContextMenu}
            relativePositioning
            open
            position={{left: 0, top: 0}}
            onClose={() => handlePopoverClose()}
            onAction={(ev) => handlePopoverAction(ev.detail.action)}
            positionParent={popoverPositionParent}
            menuItems={getPopoverMenuItems()}
          />
        </>
      )}
    </div>
  );
};

export const InsightsLineChartReact = withStyles((props: InsightsLineChartProps) => {
  const currentProject = useCurrentProject();
  const i18n = useTranslation();
  const metrics = useMetrics();
  return <InsightsLineChart currentProject={currentProject} i18n={i18n} metrics={metrics} {...props} />;
}, css);
